import React, { useEffect, useState, useMemo } from "react";
import { http } from "../lib/api";
import { ESTADOS_CARTA } from "../lib/constants";
import { toast } from "sonner";
import { Download, CheckCircle2, Clock, Search } from "lucide-react";

export default function AdminCartas() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("todas"); // todas | firmadas | pendientes
  const [q, setQ] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await http.get("/admin/cartas-asignacion");
      setItems(data || []);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const updateEstado = async (id, estado) => {
    try {
      await http.patch(`/admin/cartas-asignacion/${id}`, { estado });
      toast.success("Actualizado");
      load();
    } catch {
      toast.error("Error");
    }
  };

  const downloadPDF = async (id, name) => {
    try {
      const res = await http.post(`/cartas-asignacion/${id}/pdf`, {}, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `carta-${(name || id).replace(/\s+/g, "_")}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("No se pudo descargar");
    }
  };

  const counts = useMemo(() => ({
    todas: items.length,
    firmadas: items.filter((c) => c.estado === "firmada").length,
    pendientes: items.filter((c) => c.estado !== "firmada").length,
  }), [items]);

  const filtered = useMemo(() => {
    const ql = q.trim().toLowerCase();
    return items.filter((c) => {
      if (tab === "firmadas" && c.estado !== "firmada") return false;
      if (tab === "pendientes" && c.estado === "firmada") return false;
      if (!ql) return true;
      return [c.razon_social, c.rut_comunidad, c.representante_nombre, c.representante_email]
        .filter(Boolean)
        .some((v) => v.toLowerCase().includes(ql));
    });
  }, [items, tab, q]);

  return (
    <div data-testid="admin-cartas">
      <div className="mb-6 flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight" style={{ fontFamily: "Manrope" }}>Cartas de asignación</h1>
          <p className="text-sm mt-1" style={{ color: "var(--text-muted)" }}>Documentos firmados y enviados desde el sitio público.</p>
        </div>
        <div className="relative w-full md:w-72">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: "var(--text-soft)" }}/>
          <input
            type="text"
            placeholder="Buscar comunidad, RUT, email…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="field-input field-input--compact w-full pl-9"
            data-testid="admin-cartas-search"
          />
        </div>
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap gap-2 mb-4">
        {[
          { id: "todas", label: "Todas" },
          { id: "firmadas", label: "Firmadas" },
          { id: "pendientes", label: "Pendientes" },
        ].map((t) => {
          const active = tab === t.id;
          return (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className="px-3.5 py-1.5 rounded-full text-[12.5px] font-semibold transition"
              style={{
                background: active ? "var(--brand-navy)" : "white",
                color: active ? "white" : "var(--text-secondary)",
                border: `1px solid ${active ? "var(--brand-navy)" : "var(--border-medium)"}`,
              }}
              data-testid={`admin-cartas-tab-${t.id}`}
            >
              {t.label} <span className="opacity-70 ml-1">({counts[t.id]})</span>
            </button>
          );
        })}
      </div>

      <div className="bg-white border rounded-xl overflow-hidden" style={{ borderColor: "var(--border-subtle)" }}>
        {loading ? (
          <div className="p-10 text-center text-sm" style={{ color: "var(--text-muted)" }}>Cargando…</div>
        ) : filtered.length === 0 ? (
          <div className="p-10 text-center text-sm" style={{ color: "var(--text-muted)" }}>No hay cartas que mostrar.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="tbl">
              <thead>
                <tr>
                  <th>Comunidad</th>
                  <th>Representante</th>
                  <th>Firma</th>
                  <th>Email</th>
                  <th>Estado</th>
                  <th>Fecha</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((c) => {
                  const firmada = c.estado === "firmada";
                  return (
                    <tr key={c.id}>
                      <td className="font-semibold">
                        {c.razon_social}
                        <div className="text-[11px]" style={{ color: "var(--text-muted)" }}>{c.rut_comunidad}</div>
                      </td>
                      <td>
                        {c.representante_nombre}
                        <div className="text-[11px]" style={{ color: "var(--text-muted)" }}>{c.representante_cargo} · {c.representante_rut}</div>
                      </td>
                      <td>
                        {firmada ? (
                          <div className="inline-flex items-center gap-1.5 text-[12px] font-semibold px-2 py-1 rounded-md" style={{ background: "rgba(34,197,94,0.1)", color: "#15803d" }}>
                            <CheckCircle2 size={12}/> Firmada
                          </div>
                        ) : (
                          <div className="inline-flex items-center gap-1.5 text-[12px] font-semibold px-2 py-1 rounded-md" style={{ background: "rgba(245,158,11,0.1)", color: "#b45309" }}>
                            <Clock size={12}/> Pendiente
                          </div>
                        )}
                        {firmada && c.firma_imagen_base64 ? (
                          <div className="mt-1.5 inline-block rounded border" style={{ borderColor: "var(--border-subtle)", background: "white" }}>
                            <img
                              src={`data:image/png;base64,${c.firma_imagen_base64}`}
                              alt="firma"
                              className="h-8 w-auto object-contain"
                            />
                          </div>
                        ) : null}
                      </td>
                      <td className="text-[13px]">{c.representante_email}</td>
                      <td>
                        <select
                          className="text-[12px] px-2 py-1 rounded"
                          style={{ border: "1px solid var(--border-medium)", background: "white" }}
                          value={c.estado}
                          onChange={(e) => updateEstado(c.id, e.target.value)}
                        >
                          {ESTADOS_CARTA.map((e) => <option key={e} value={e}>{e}</option>)}
                        </select>
                      </td>
                      <td className="text-[12px]" style={{ color: "var(--text-muted)" }}>
                        {(c.firmada_at || c.created_at || "").slice(0,10)}
                      </td>
                      <td>
                        <button
                          className="text-sm font-semibold inline-flex items-center gap-1"
                          style={{ color: "var(--brand-accent)" }}
                          onClick={() => downloadPDF(c.id, c.razon_social)}
                          data-testid={`pdf-${c.id}`}
                        >
                          <Download size={14}/>PDF
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
